import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import styles from './UserSettingsPage.module.scss';
import {
  IconUser,
  IconHistory,
  IconBell,
  IconFileExport,
  IconHelp,
  IconArrowLeft,
  IconCamera,
  IconCheck,
  IconUserPlus,
  IconLanguage,
  IconTool
} from '@tabler/icons-react';
import { useAuth } from '../../lib/AuthContext';
import { useGetCustomerId } from '../../api/customer-api/customer-api';
import { useGetAuthUser, usePostAuthUserPassword, usePutAuthUser } from '../../api/auth-api/auth-api';
import { useProjects } from '../../hooks/useProjects';
import { PermissionKey, UserDto, UserRoles } from '../../api/model';
import { useNotifications } from '../../hooks/useNotifications';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';
import { useQueryClient } from '@tanstack/react-query';
import ManageUsers from '../../components/ManageUsers/ManageUsers';
import AdminConsole from '../../components/AdminConsole/AdminConsole';
import AuditLog from '../../components/AuditLog/AuditLog';
import { IconShieldCog } from '@tabler/icons-react';

type SettingsSection =
    'profile'
    | 'audit'
    | 'notifications'
    | 'language'
    | 'export'
    | 'help'
    | 'manageUsers'
    | 'adminConsole';

// Sections that are still preview / mock only.
const UNDER_DEVELOPMENT_SECTIONS: SettingsSection[] = ['notifications', 'export', 'help'];

const UserSettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation('settings');
  const navigate = useNavigate();
  const { customerId, logout } = useAuth();
  const passwordMutation = usePostAuthUserPassword();
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordMessage, setPasswordMessage] = useState<{ ok: boolean; text: string } | null>(null);

  // Changing the password invalidates every session of the user (token version),
  // so a successful change logs this one out too, after saying so.
  const handleChangePassword = () => {
    if (passwordForm.next.length < 8) {
      setPasswordMessage({ ok: false, text: t('user.profile.password.tooShort') });
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordMessage({ ok: false, text: t('user.profile.password.mismatch') });
      return;
    }
    passwordMutation.mutate(
      { data: { current: passwordForm.current, next: passwordForm.next } },
      {
        onSuccess: () => {
          setPasswordForm({ current: '', next: '', confirm: '' });
          setPasswordMessage({ ok: true, text: t('user.profile.password.changed') });
          setTimeout(logout, 2500);
        },
        onError: () => setPasswordMessage({ ok: false, text: t('user.profile.password.wrongCurrent') }),
      },
    );
  };
  const [activeSection, setActiveSection] = useState<SettingsSection>('profile');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();

  const { data: userData, isLoading: isLoadingUser } = useGetAuthUser();
  
  // Fetch customer data
  const { data: customerData, isLoading: isLoadingCustomer } = useGetCustomerId(
    customerId || 0,
    {
      query: {
        enabled: !!customerId,
      }
    }
  );

  const { data: projectsData = [], isLoading: isLoadingProjects } = useProjects();

  const updateUserMutation = usePutAuthUser({
    mutation: {
      onSuccess: () => {
        showSuccess(t('user.profile.feedback.updated'), t('user.profile.feedback.updatedMsg'));
        queryClient.invalidateQueries({ queryKey: ['/auth/user'] });
      },
      onError: (error: any) => {
        showError(t('user.profile.feedback.updateFailed'), error.message || t('user.profile.feedback.updateFailedMsg'));
      },
    },
  });

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    avatar: '',
  });


  useEffect(() => {
    if (userData) {
      setProfileData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.username || '',
        avatar: userData.avatar || '',
      });
    }
  }, [userData]);

  const { can, isSystemAdmin } = useCurrentUserPermissions();
  // system admins manage users from Admin Console
  const canManageUsers = !isSystemAdmin && can(PermissionKey.USER_MANAGEMENT, 'READ');
  const canOpenAdminConsole = isSystemAdmin;

  const menuItems = [
    { id: 'profile' as SettingsSection, label: t('user.menu.profile'), icon: IconUser },
    ...(canManageUsers ? [{ id: 'manageUsers' as SettingsSection, label: t('user.menu.manageUsers'), icon: IconUserPlus }] : []),
    ...(canOpenAdminConsole ? [{ id: 'adminConsole' as SettingsSection, label: t('user.menu.adminConsole'), icon: IconShieldCog }] : []),
    ...((isSystemAdmin || can(PermissionKey.VIEW_AUDIT_TRAIL, 'READ')) ? [{ id: 'audit' as SettingsSection, label: t('user.menu.audit'), icon: IconHistory }] : []),
    { id: 'notifications' as SettingsSection, label: t('user.menu.notifications'), icon: IconBell },
    { id: 'language' as SettingsSection, label: t('user.menu.language'), icon: IconLanguage },
    { id: 'export' as SettingsSection, label: t('user.menu.export'), icon: IconFileExport },
    { id: 'help' as SettingsSection, label: t('user.menu.help'), icon: IconHelp },
  ];

  const renderUnderDevelopmentBanner = () => (
    <div className={styles.underDevelopmentBanner}>
      <IconTool size={18} className={styles.underDevelopmentIcon} />
      <div className={styles.underDevelopmentText}>
        <span className={styles.underDevelopmentTitle}>{t('user.underDevelopment.title')}</span>
        <span className={styles.underDevelopmentMessage}>{t('user.underDevelopment.message')}</span>
      </div>
    </div>
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = () => {
    if (!userData?.id) {
      showError(t('user.profile.feedback.errorTitle'), t('user.profile.feedback.userNotLoaded'));
      return;
    }

    const updatedUser: UserDto = {
      ...userData,
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      avatar: profileData.avatar || userData.avatar,
    };

    updateUserMutation.mutate({ data: updatedUser });
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showError(t('user.profile.feedback.fileTooLarge'), t('user.profile.feedback.fileTooLargeMsg'));
        return;
      }

      // Validate type of file
      if (!file.type.startsWith('image/')) {
        showError(t('user.profile.feedback.invalidFileType'), t('user.profile.feedback.invalidFileTypeMsg'));
        return;
      }

      // convert base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setProfileData(prev => ({
          ...prev,
          avatar: base64String
        }));
        showSuccess(t('user.profile.feedback.avatarSelected'), t('user.profile.feedback.avatarSelectedMsg'));
      };
      reader.onerror = () => {
        showError(t('user.profile.feedback.uploadFailed'), t('user.profile.feedback.uploadFailedMsg'));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGoBack = () => {
    navigate({ to: '/dashboard' });
  };

  const LICENSE_INFO: Record<string, { name: string; maxUsers: number; maxProjects: number }> = {
    PILOT: { name: 'Pilot', maxUsers: 5, maxProjects: 3 },
    BASIC: { name: 'Basic', maxUsers: 20, maxProjects: 10 },
    PRO: { name: 'Pro', maxUsers: 100, maxProjects: 50 },
    ENTERPRISE: { name: 'Enterprise', maxUsers: -1, maxProjects: -1 },
  };

  const licenseInfo = userData?.licenseType ? LICENSE_INFO[userData.licenseType] : null;

  const renderProfileSection = () => (
    <div className={styles.sectionContent}>
      <h2 className={styles.sectionTitle}>{t('user.profile.sectionTitle')}</h2>
      <p className={styles.sectionDescription}>
        {t('user.profile.sectionDescription')}
      </p>

      {isLoadingUser && (
        <div className={styles.infoSection}>
          <p className={styles.loadingText}>{t('user.profile.loadingUser')}</p>
        </div>
      )}

      {customerData && (
        <div className={styles.infoSection}>
          <h3 className={styles.infoTitle}>{t('user.profile.company.title')}</h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{t('user.profile.company.name')}</span>
              <span className={styles.infoValue}>{customerData.name || 'N/A'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{t('user.profile.company.tenantId')}</span>
              <span className={styles.infoValue}>{customerData.tenantId || 'N/A'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{t('user.profile.company.size')}</span>
              <span className={styles.infoValue}>
                {customerData.companySize?.replace(/_/g, ' ') || 'N/A'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{t('user.profile.company.industry')}</span>
              <span className={styles.infoValue}>
                {customerData.industrySector?.replace(/_/g, ' ') || 'N/A'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{t('user.profile.company.market')}</span>
              <span className={styles.infoValue}>
                {customerData.coreMarket?.replace(/_/g, ' ') || 'N/A'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{t('user.profile.company.created')}</span>
              <span className={styles.infoValue}>
                {customerData.created 
                  ? new Date(customerData.created as any).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      {isLoadingCustomer && (
        <div className={styles.infoSection}>
          <p className={styles.loadingText}>{t('user.profile.loadingCustomer')}</p>
        </div>
      )}

      <div className={styles.profileForm}>
        <div className={styles.avatarSection}>
          <div className={styles.avatarWrapper}>
            {(profileData.avatar || userData?.avatar) ? (
              <img
                src={profileData.avatar || userData?.avatar}
                alt="Profile"
                className={styles.avatar}
              />
            ) : (
              <div className={styles.avatarInitials}>
                {`${(userData?.firstName?.[0] ?? '').toUpperCase()}${(userData?.lastName?.[0] ?? '').toUpperCase()}` || '?'}
              </div>
            )}
            <label htmlFor="avatar-upload" className={styles.avatarUploadButton}>
              <IconCamera size={20} />
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className={styles.avatarInput}
              />
            </label>
          </div>
          <div className={styles.avatarInfo}>
            <h3 className={styles.avatarTitle}>{t('user.profile.avatar.title')}</h3>
            <p className={styles.avatarDescription}>
              {t('user.profile.avatar.description')}
            </p>
          </div>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('user.profile.fields.firstName')}</label>
            <input
              type="text"
              name="firstName"
              value={profileData.firstName}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t('user.profile.fields.firstNamePlaceholder')}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>{t('user.profile.fields.lastName')}</label>
            <input
              type="text"
              name="lastName"
              value={profileData.lastName}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t('user.profile.fields.lastNamePlaceholder')}
            />
          </div>

          <div className={`${styles.formGroup} ${styles.fullWidth}`}>
            <label className={styles.label}>{t('user.profile.fields.email')}</label>
            <input
              type="email"
              name="email"
              value={profileData.email}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t('user.profile.fields.emailPlaceholder')}
              disabled
            />
          </div>
        </div>

        <div className={styles.accountInfoCard}>
          <h4 className={styles.accountInfoTitle}>{t('user.profile.accountInfo', 'Account Information')}</h4>
          <div className={styles.accountInfoGrid}>
            <div className={styles.accountInfoItem}>
              <span className={styles.accountInfoLabel}>{t('user.profile.fields.role', 'Role')}</span>
              <span className={styles.accountInfoValue}>
                {/* 'SYSTEM_ADMIN' used to be tested here too, but UserRoles only has
                  * ADMIN, USER and SUPER_ADMIN - the comparison could never be true and
                  * tsc flagged it. The flag from useCurrentUserPermissions is the real
                  * answer to "is this a system admin", so it stands in for the dead half. */}
                {userData?.role === UserRoles.SUPER_ADMIN || isSystemAdmin
                  ? 'System Admin'
                  : userData?.tenantRoleDisplayName || 'N/A'}
              </span>
            </div>
            {!isSystemAdmin && (
              <>
                <div className={styles.accountInfoItem}>
                  <span className={styles.accountInfoLabel}>{t('user.profile.fields.licenseType', 'Plan')}</span>
                  <span className={styles.accountInfoValue}>
                    {licenseInfo ? licenseInfo.name : 'N/A'}
                  </span>
                </div>
                <div className={styles.accountInfoItem}>
                  <span className={styles.accountInfoLabel}>{t('user.profile.fields.licenseExpiration', 'Expires')}</span>
                  <span className={styles.accountInfoValue}>
                    {userData?.licenseExpirationDate
                      ? new Date(userData.licenseExpirationDate as any).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
                {licenseInfo && (
                  <div className={styles.accountInfoItem}>
                    <span className={styles.accountInfoLabel}>Max Users</span>
                    <span className={styles.accountInfoValue}>
                      {licenseInfo.maxUsers === -1 ? 'Unlimited' : licenseInfo.maxUsers}
                    </span>
                  </div>
                )}
                {licenseInfo && (
                  <div className={styles.accountInfoItem}>
                    <span className={styles.accountInfoLabel}>Max Projects</span>
                    <span className={styles.accountInfoValue}>
                      {licenseInfo.maxProjects === -1 ? 'Unlimited' : licenseInfo.maxProjects}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>{t('user.profile.fields.accessibleProjects')}</label>
          {isLoadingProjects ? (
            <p className={styles.loadingText}>{t('user.profile.loadingProjects')}</p>
          ) : projectsData.length > 0 ? (
            <div className={styles.projectsList}>
              {projectsData.map((project) => (
                <div key={project.id} className={styles.projectItem}>
                  <IconCheck size={16} className={styles.projectIcon} />
                  <span className={styles.projectName}>{project.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.infoValue}>{t('user.profile.noProjects')}</p>
          )}
        </div>

        <div className={styles.infoSection}>
          <h3 className={styles.infoTitle}>{t('user.profile.password.title')}</h3>
          <div className={styles.formGrid}>
            {(['current', 'next', 'confirm'] as const).map((field) => (
              <div key={field} className={styles.formGroup}>
                <label className={styles.label}>{t(`user.profile.password.${field}`)}</label>
                <input
                  type="password"
                  autoComplete={field === 'current' ? 'current-password' : 'new-password'}
                  value={passwordForm[field]}
                  onChange={(e) => {
                    setPasswordForm((prev) => ({ ...prev, [field]: e.target.value }));
                    setPasswordMessage(null);
                  }}
                  className={styles.input}
                />
              </div>
            ))}
          </div>
          {passwordMessage && (
            <p style={{ margin: '8px 0', color: passwordMessage.ok ? '#0ca30c' : '#d03b3b', fontSize: 13 }}>
              {passwordMessage.text}
            </p>
          )}
          <button
            className={styles.saveButton}
            onClick={handleChangePassword}
            disabled={passwordMutation.isPending || !passwordForm.current || !passwordForm.next || !passwordForm.confirm}
          >
            {t('user.profile.password.submit')}
          </button>
        </div>

        <div className={styles.formActions}>
          <button className={styles.cancelButton} onClick={handleGoBack}>
            {t('user.profile.actions.cancel')}
          </button>
          <button 
            className={styles.saveButton} 
            onClick={handleSaveProfile}
            disabled={updateUserMutation.isPending || isLoadingUser}
          >
            <IconCheck size={18} />
            {updateUserMutation.isPending ? t('user.profile.actions.saving') : t('user.profile.actions.save')}
          </button>
        </div>
      </div>
    </div>
  );

    const renderAuditSection = () => <div className={styles.sectionContent}><AuditLog/></div>;

  const renderNotificationsSection = () => (
    <div className={styles.sectionContent}>
      {renderUnderDevelopmentBanner()}
      <h2 className={styles.sectionTitle}>{t('user.notifications.title')}</h2>
      <p className={styles.sectionDescription}>
        {t('user.notifications.description')}
      </p>

      <div className={styles.notificationSettings}>
        <div className={styles.notificationGroup}>
          <h3 className={styles.notificationGroupTitle}>{t('user.notifications.email')}</h3>
          {[
            { id: 'email-projects', label: t('user.notifications.items.projectUpdates'), enabled: true },
            { id: 'email-tasks', label: t('user.notifications.items.taskAssignments'), enabled: true },
            { id: 'email-comments', label: t('user.notifications.items.comments'), enabled: false },
          ].map((setting) => (
            <div key={setting.id} className={styles.notificationItem}>
              <div className={styles.notificationInfo}>
                <span className={styles.notificationLabel}>{setting.label}</span>
              </div>
              <label className={styles.switch}>
                <input type="checkbox" defaultChecked={setting.enabled} />
                <span className={styles.slider}></span>
              </label>
            </div>
          ))}
        </div>

        <div className={styles.notificationGroup}>
          <h3 className={styles.notificationGroupTitle}>{t('user.notifications.push')}</h3>
          {[
            { id: 'push-urgent', label: t('user.notifications.items.projectUpdates'), enabled: true },
            { id: 'push-deadlines', label: t('user.notifications.items.deadlines'), enabled: true },
            { id: 'push-messages', label: t('user.notifications.items.directMessages'), enabled: false },
          ].map((setting) => (
            <div key={setting.id} className={styles.notificationItem}>
              <div className={styles.notificationInfo}>
                <span className={styles.notificationLabel}>{setting.label}</span>
              </div>
              <label className={styles.switch}>
                <input type="checkbox" defaultChecked={setting.enabled} />
                <span className={styles.slider}></span>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderExportSection = () => (
    <div className={styles.sectionContent}>
      {renderUnderDevelopmentBanner()}
      <h2 className={styles.sectionTitle}>{t('user.export.title')}</h2>
      <p className={styles.sectionDescription}>
        {t('user.export.description')}
      </p>

      <div className={styles.exportOptions}>
        <div className={styles.exportCard}>
          <h3 className={styles.exportCardTitle}>{t('user.export.userData')}</h3>
          <p className={styles.exportCardDescription}>
            {t('user.export.userDataDesc')}
          </p>
          <button className={styles.exportButton}>{t('user.export.asJson')}</button>
        </div>

        <div className={styles.exportCard}>
          <h3 className={styles.exportCardTitle}>{t('user.export.projects')}</h3>
          <p className={styles.exportCardDescription}>
            {t('user.export.projectsDesc')}
          </p>
          <button className={styles.exportButton}>{t('user.export.asCsv')}</button>
        </div>

        <div className={styles.exportCard}>
          <h3 className={styles.exportCardTitle}>{t('user.export.report')}</h3>
          <p className={styles.exportCardDescription}>
            {t('user.export.reportDesc')}
          </p>
          <button className={styles.exportButton}>{t('user.export.asPdf')}</button>
        </div>
      </div>
    </div>
  );

  const renderHelpSection = () => (
    <div className={styles.sectionContent}>
      {renderUnderDevelopmentBanner()}
      <h2 className={styles.sectionTitle}>{t('user.help.title')}</h2>
      <p className={styles.sectionDescription}>
        {t('user.help.description')}
      </p>

      <div className={styles.helpContent}>
        <div className={styles.helpCard}>
          <h3 className={styles.helpCardTitle}>{t('user.help.docs')}</h3>
          <p className={styles.helpCardDescription}>
            {t('user.help.docsDesc')}
          </p>
          <a href="#" className={styles.helpLink}>{t('user.help.viewDocs')}</a>
        </div>

        <div className={styles.helpCard}>
          <h3 className={styles.helpCardTitle}>{t('user.help.contact')}</h3>
          <p className={styles.helpCardDescription}>
            {t('user.help.contactDesc')}
          </p>
            <a href="mailto:support@qlario.io" className={styles.helpLink}>support@qlario.io</a>
        </div>

        <div className={styles.helpCard}>
          <h3 className={styles.helpCardTitle}>{t('user.help.faq')}</h3>
          <p className={styles.helpCardDescription}>
            {t('user.help.faqDesc')}
          </p>
          <a href="#" className={styles.helpLink}>{t('user.help.browseFaq')}</a>
        </div>

        <div className={styles.helpCard}>
          <h3 className={styles.helpCardTitle}>{t('user.help.version')}</h3>
          <p className={styles.helpCardDescription}>
            {t('user.help.versionLine')}
          </p>
          <p className={styles.helpCardDescription}>
            {t('user.help.lastUpdated')}
          </p>
        </div>
      </div>
    </div>
  );

  const renderManageUsersSection = () => (
    <div className={styles.sectionContent}>
      <ManageUsers />
    </div>
  );

  const renderAdminConsoleSection = () => (
    <div className={styles.sectionContent}>
      <AdminConsole />
    </div>
  );

  const renderLanguageSection = () => (
    <div className={styles.sectionContent}>
      <h2 className={styles.sectionTitle}>{t('user.language.title')}</h2>
      <p className={styles.sectionDescription}>{t('user.language.description')}</p>
      <div className={styles.languageOptions}>
        {[
          { code: 'en', name: 'English', native: 'English' },
          { code: 'de', name: 'Deutsch', native: 'German' },
        ].map(lang => (
          <button
            key={lang.code}
            className={`${styles.languageCard} ${i18n.language === lang.code ? styles.languageCardActive : ''}`}
            onClick={() => i18n.changeLanguage(lang.code)}
          >
            <div className={styles.languageCardLeft}>
              <span className={styles.languageCardAvatar}>{lang.code.toUpperCase()}</span>
              <div className={styles.languageCardInfo}>
                <span className={styles.languageCardName}>{lang.name}</span>
                <span className={styles.languageCardNative}>{lang.native}</span>
              </div>
            </div>
            {i18n.language === lang.code && <IconCheck size={18} className={styles.languageCardCheck} />}
          </button>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection();
      case 'manageUsers':
        return renderManageUsersSection();
      case 'adminConsole':
        return renderAdminConsoleSection();
        case 'audit':
            return renderAuditSection();
      case 'notifications':
        return renderNotificationsSection();
      case 'language':
        return renderLanguageSection();
      case 'export':
        return renderExportSection();
      case 'help':
        return renderHelpSection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className={styles.settingsPage}>
      <div className={styles.settingsContainer}>
        <div className={styles.settingsHeader}>
          <button className={styles.backButton} onClick={handleGoBack}>
            <IconArrowLeft size={20} />
            <span>{t('user.back')}</span>
          </button>
          <h1 className={styles.pageTitle}>{t('user.pageTitle')}</h1>
        </div>

        <div className={styles.settingsLayout}>
          <div className={styles.settingsMenu}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isSoon = UNDER_DEVELOPMENT_SECTIONS.includes(item.id);
              return (
                <button
                  key={item.id}
                  className={`${styles.menuItem} ${activeSection === item.id ? styles.active : ''}`}
                  onClick={() => setActiveSection(item.id)}
                >
                  <Icon size={20} className={styles.menuIcon} />
                  <span className={styles.menuLabel}>{item.label}</span>
                  {isSoon && <span className={styles.soonBadge}>{t('user.menu.soon')}</span>}
                </button>
              );
            })}
          </div>

          <div className={styles.settingsContent}>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsPage;
